/**
 * Audio Playback Service
 * Handles real-time audio playback for TTS responses
 */

import { audioVolumeManager } from './audioVolumeManager';

class AudioPlaybackService {
  constructor() {
    this.audioContext = null;
    this.audioQueue = [];
    this.isPlaying = false;
    this.websocket = null;
    this.isConnected = false;
    this.onPlaybackComplete = null; // Callback when all audio finishes
    this.streamEnded = false; // Track if stream has ended
    this.gainNode = null; // For volume control
    this.stateChangeHandler = null; // Store handler for cleanup
    // Sequence-based ordering for parallel TTS
    this.pendingAudio = {}; // {sequence: audioBuffer} - holds out-of-order audio
    this.nextSequenceToPlay = 1; // Track expected sequence number
  }

  /**
   * Initialize audio context with volume control
   */
  async initialize() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Monitor audio context state changes (with null check)
      this.stateChangeHandler = () => {
        if (this.audioContext) {
          console.log(`🎵 AudioContext state changed to: ${this.audioContext.state}`);
          if (this.audioContext.state === 'suspended') {
            console.log('⚠️ AudioContext suspended, will resume on next play');
          }
        }
      };
      
      this.audioContext.addEventListener('statechange', this.stateChangeHandler);
      
      console.log('🎵 Audio context initialized');
    }

    // Always ensure gain node exists and is properly connected
    this.ensureGainNode();

    // Resume audio context if it was suspended
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      console.log('▶️ AudioContext resumed');
    }

    return true;
  }

  /**
   * Ensure gain node exists and is properly connected
   * Call this before playing audio to handle disconnections
   */
  ensureGainNode() {
    if (!this.audioContext) return;

    // Check if gain node exists and is properly connected
    if (!this.gainNode) {
      console.log('🔧 Creating gain node...');
      this.gainNode = this.audioContext.createGain();
      this.gainNode.connect(this.audioContext.destination);
      
      // Set volume from audioVolumeManager
      const appVolume = audioVolumeManager.getVolume();
      this.gainNode.gain.value = appVolume / 100;
      
      // Listen for volume changes
      audioVolumeManager.on('volumeChanged', this.handleVolumeChange.bind(this));
    }
  }

  /**
   * Handle volume changes from audioVolumeManager
   */
  handleVolumeChange(volume) {
    if (this.gainNode) {
      this.gainNode.gain.value = volume / 100;
    }
  }

  /**
   * Connect to TTS WebSocket for audio streaming
   */
  connectToTTS(ttsServerUrl = 'ws://localhost:8002') {
    return new Promise((resolve, reject) => {
      try {
        console.log(`🔌 Connecting to TTS WebSocket: ${ttsServerUrl}/ws/audio-stream`);
        
        this.websocket = new WebSocket(`${ttsServerUrl}/ws/audio-stream`);
        this.websocket.binaryType = 'arraybuffer';

        this.websocket.onopen = () => {
          console.log('✅ Connected to TTS WebSocket');
          this.isConnected = true;
          resolve();
        };

        this.websocket.onmessage = async (event) => {
          // Received audio chunk from TTS server
          // Check if it's JSON (with sequence) or raw bytes (legacy)
          if (typeof event.data === 'string') {
            // JSON message with sequence number
            try {
              const message = JSON.parse(event.data);
              if (message.audio && message.sequence !== undefined) {
                // Decode base64 audio
                const audioBytes = Uint8Array.from(atob(message.audio), c => c.charCodeAt(0));
                await this.playAudioChunk(audioBytes.buffer, message.sequence);
              }
            } catch (e) {
              console.error('Failed to parse audio message:', e);
            }
          } else {
            // Raw binary audio (legacy, no sequence)
            await this.playAudioChunk(event.data);
          }
        };

        this.websocket.onerror = (error) => {
          console.error('❌ TTS WebSocket error:', error);
          this.isConnected = false;
          reject(error);
        };

        this.websocket.onclose = () => {
          console.log('🔌 TTS WebSocket closed');
          this.isConnected = false;
        };

      } catch (error) {
        console.error('Failed to connect to TTS WebSocket:', error);
        reject(error);
      }
    });
  }

  /**
   * Send token to TTS server for audio generation
   */
  sendToken(token) {
    if (this.isConnected && this.websocket) {
      this.websocket.send(JSON.stringify({ token }));
    }
  }

  /**
   * Flush remaining buffer (end of response)
   */
  flush() {
    if (this.isConnected && this.websocket) {
      this.websocket.send(JSON.stringify({ flush: true }));
    }
  }

  /**
   * Mark stream as ended (called when LLM response completes)
   */
  markStreamEnded() {
    this.streamEnded = true;
    console.log('🏁 Audio stream marked as ended');
    
    // If queue is already empty, trigger completion immediately
    if (this.audioQueue.length === 0 && !this.isPlaying && this.onPlaybackComplete) {
      console.log('✅ All audio playback completed (immediate)');
      this.onPlaybackComplete();
      this.streamEnded = false;
    }
  }

  /**
   * Play audio chunk received from TTS (with optional sequence number)
   */
  async playAudioChunk(audioData, sequence = null) {
    if (!this.audioContext) {
      await this.initialize();
    }

    try {
      // Decode audio data (WAV format from TTS server)
      const audioBuffer = await this.audioContext.decodeAudioData(audioData);
      
      // If no sequence number (legacy), play immediately
      if (sequence === null) {
        this.audioQueue.push(audioBuffer);
        if (!this.isPlaying) {
          this.playNextInQueue();
        }
        return;
      }
      
      // SEQUENCE-BASED ORDERING
      // Check if this is the next expected sequence
      if (sequence === this.nextSequenceToPlay) {
        console.log(`🔊 Audio sequence ${sequence} arrived in order - playing immediately`);
        
        // Play this audio
        this.audioQueue.push(audioBuffer);
        this.nextSequenceToPlay++;
        
        // Check if any pending audio can now be played
        while (this.pendingAudio[this.nextSequenceToPlay]) {
          console.log(`🔊 Playing queued audio sequence ${this.nextSequenceToPlay}`);
          this.audioQueue.push(this.pendingAudio[this.nextSequenceToPlay]);
          delete this.pendingAudio[this.nextSequenceToPlay];
          this.nextSequenceToPlay++;
        }
        
        // Start playing if not already playing
        if (!this.isPlaying) {
          this.playNextInQueue();
        }
      } else if (sequence > this.nextSequenceToPlay) {
        // This audio arrived early - store it for later
        console.log(`⏳ Audio sequence ${sequence} arrived early (expecting ${this.nextSequenceToPlay}) - holding...`);
        this.pendingAudio[sequence] = audioBuffer;
      } else {
        // This audio arrived late (already played) - ignore
        console.warn(`⚠️ Audio sequence ${sequence} arrived late (already at ${this.nextSequenceToPlay}) - ignoring`);
      }

    } catch (error) {
      console.error('Failed to decode/play audio:', error);
    }
  }

  /**
   * Play next audio buffer in queue
   */
  async playNextInQueue() {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      
      // If stream has ended and queue is empty, all audio is complete
      if (this.streamEnded && this.onPlaybackComplete) {
        console.log('✅ All audio playback completed');
        this.onPlaybackComplete();
        this.streamEnded = false; // Reset for next session
      }
      
      return;
    }

    this.isPlaying = true;
    const audioBuffer = this.audioQueue.shift();

    try {
      // Ensure audio context is ready and gain node exists
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
        console.log('▶️ AudioContext resumed before playback');
      }
      
      // Ensure gain node exists before playing
      this.ensureGainNode();
      
      // Create audio source
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      
      // Connect through gain node for volume control
      if (this.gainNode) {
        source.connect(this.gainNode);
      } else {
        source.connect(this.audioContext.destination);
      }

      // When this audio finishes, play next in queue
      source.onended = () => {
        this.playNextInQueue();
      };

      // Start playback
      source.start(0);
      console.log('🔊 Playing audio chunk');

    } catch (error) {
      console.error('Failed to play audio chunk:', error);
      this.playNextInQueue(); // Try next chunk
    }
  }

  /**
   * Disconnect from TTS WebSocket
   */
  disconnect() {
    if (this.websocket) {
      console.log('🔌 Disconnecting from TTS WebSocket');
      this.websocket.close();
      this.websocket = null;
      this.isConnected = false;
    }
  }

  /**
   * Clear audio queue and stop playback
   */
  stop() {
    this.audioQueue = [];
    this.pendingAudio = {};
    this.nextSequenceToPlay = 1;
    this.isPlaying = false;
    console.log('⏹️ Audio playback stopped');
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stop();
    this.disconnect();
    
    // Remove volume change listener
    if (audioVolumeManager) {
      audioVolumeManager.removeListener('volumeChanged', this.handleVolumeChange);
    }
    
    // Disconnect and clear gain node
    if (this.gainNode) {
      try {
        this.gainNode.disconnect();
      } catch (e) {
        // Already disconnected
      }
      this.gainNode = null;
    }
    
    // Remove statechange listener and close audio context
    if (this.audioContext) {
      if (this.stateChangeHandler) {
        this.audioContext.removeEventListener('statechange', this.stateChangeHandler);
        this.stateChangeHandler = null;
      }
      this.audioContext.close();
      this.audioContext = null;
    }
    
    this.onPlaybackComplete = null;
    this.streamEnded = false;
    this.pendingAudio = {};
    this.nextSequenceToPlay = 1;
    
    console.log('🧹 Audio playback service cleaned up');
  }
}

// Export singleton instance
export const audioPlaybackService = new AudioPlaybackService();
